import {
  Square,
  Heading,
  Stack,
  Row,
  Column,
  Button,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams, useHistory } from 'react-router-dom';
import { Input } from 'src/components/Input/Input';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { CookbookTextareaWrapper } from 'src/components/CookbookTextareaWrapper/CookbookTextareaWrapper';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { getHomeBreadcrumb } from 'src/constants/urls';

const DESCRIPTION_MAX_LENGTH = 300;

interface FormFields {
  id?: string;
  name: string;
  description: string;
}

export interface IEnterpriseManageGroup {
  isEditing?: boolean;
}

export default function EnterpriseManageGroup({
  isEditing,
}: IEnterpriseManageGroup): JSX.Element {
  const { corpid, groupId } = useParams<{ corpid: string; groupId: string }>();
  const { push } = useHistory();

  const { sendTealiumPageView } = useAnalytics();
  useEffect(() => {
    if (isEditing && corpid && groupId) {
      getGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, corpid, groupId]);

  useEffect(() => {
    const pagesubgroup = isEditing
      ? 'manage group_edit'
      : 'manage group_create';

    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const { control, register, handleSubmit, formState, setValue } =
    useForm<FormFields>({
      mode: 'all',
    });

  const { isValid, isSubmitting, errors } = formState;

  async function getGroup() {
    try {
      const { name, description, id } = await sdk.http.get(
        `/corp-admin/${corpid}/groups/${groupId}`
      );
      setValue('name', name, { shouldValidate: true });
      setValue('description', description);
      register({ name: 'id', value: id });
    } catch (e) {
      pushErrorFromAPI(e);
    }
  }

  async function submitGroup({
    id,
    name,
    description,
  }: FormFields): Promise<void> {
    const actionText = isEditing ? 'updated' : 'added';
    const payload = {
      name,
      description,
    };

    try {
      let groupId = id;
      if (isEditing) {
        await sdk.http.put(`/corp-admin/${corpid}/groups/${id}`, payload);
      } else {
        const response = await sdk.http.post(
          `/corp-admin/${corpid}/groups`,
          payload
        );
        groupId = response.id;
      }
      push(`/corporate/enterprise/${corpid}/groups/${groupId}/details`);
      pushSuccess(`${name} was ${actionText}`);
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${name} was not ${actionText}. Try again.`,
      });
    }
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">
            {isEditing ? 'Edit Group' : 'Add new group'}
          </Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
            {
              label: 'Groups',
              href: `/corporate/enterprise/${corpid}/groups`,
              useRouterLink: true,
            },
          ]}
        />
      </PageHeader>
      <form>
        <Stack y="md">
          <PageSection>
            <Stack y="sm">
              <Heading variant="h4">Group Information</Heading>
            </Stack>
            <Stack y="sm">
              <Row>
                <Column tablet={6} desktop={5}>
                  <Input
                    id="name"
                    name="name"
                    label="Group name *"
                    placeholder="e.g. Burger Club"
                    maxLength={300}
                    error={errors.name}
                    ref={register({
                      required: 'Group name is required',
                      maxLength: {
                        value: 300,
                        message: 'Group name should be 300 characters or less',
                      },
                    })}
                  />
                </Column>
              </Row>
            </Stack>
            <Row>
              <Column tablet={6} desktop={5}>
                <CookbookTextareaWrapper
                  control={control}
                  error={errors.description}
                  id="description"
                  name="description"
                  label="Description"
                  placeholder="People who love burgers"
                  rules={{
                    maxLength: DESCRIPTION_MAX_LENGTH,
                  }}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  minHeight={100}
                  blockWidth
                />
              </Column>
            </Row>
          </PageSection>
        </Stack>
        <Row gutter={8}>
          <Column gutter={8} tablet={4} desktop={3}>
            <Button
              variant="primary"
              block
              buttonSize="medium"
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit(submitGroup)}
            >
              {isEditing ? 'Update group' : 'Add group'}
            </Button>
          </Column>
          <Column
            gutter={8}
            tablet={4}
            desktop={3}
            style={{ textAlign: 'center' }}
          >
            <RouterButtonLink
              href={`/corporate/enterprise/${corpid}/groups`}
              buttonSize="medium"
              buttonVariant="secondary"
              useBlockButton
              isButtonDisabled={isSubmitting}
            >
              Cancel
            </RouterButtonLink>
          </Column>
        </Row>
      </form>
    </Square>
  );
}
